import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import config from "../Config/config";
import { buildSectionTree, checkDocsPermission } from "./infoShared";

const InfoView = () => {
  const { docId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [permissionLoading, setPermissionLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const run = async () => {
      const permission = await checkDocsPermission();
      setHasPermission(permission.allowed);
      setPermissionError(permission.error);
      setPermissionLoading(false);
    };

    run();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [docResponse, sectionResponse] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/docs/${docId}`),
        axios.get(`${config.API_BASE_URL}/docs/${docId}/sections`),
      ]);

      setDoc(docResponse.data || null);
      const tree = buildSectionTree(sectionResponse.data || []);
      setSections(tree);

      const initialExpanded = {};
      tree.forEach((item) => {
        initialExpanded[item.id] = true;
      });
      setExpanded(initialExpanded);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const outline = useMemo(() => {
    const nodes = [];
    const walk = (items, depth) => {
      items.forEach((item) => {
        nodes.push({ id: item.id, title: item.title, depth, order: item.order || 0 });
        if (item.subsections?.length) {
          walk(item.subsections, depth + 1);
        }
      });
    };

    walk(sections, 0);
    return nodes;
  }, [sections]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const deleteDoc = async () => {
    if (!window.confirm("Delete this document and all nested sections?")) return;

    try {
      await axios.delete(`${config.API_BASE_URL}/docs/${docId}`);
      navigate("/docs");
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm("Delete this section?")) return;

    try {
      await axios.delete(`${config.API_BASE_URL}/docs/${docId}/sections/${sectionId}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const renderSection = (section, depth = 0) => {
    const isOpen = expanded[section.id] ?? false;

    return (
      <li key={section.id} className="space-y-3">
        <article
          id={`section-${section.id}`}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          style={{ marginLeft: `${depth * 14}px` }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <button
              type="button"
              onClick={() => toggle(section.id)}
              className="text-left"
            >
              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                Section {Number(section.order) || 0}
              </p>
              <h3 className="text-lg font-semibold text-slate-900 mt-0.5">
                {section.title || "Untitled Section"}
              </h3>
              {section.subsections?.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  {isOpen ? "Collapse" : "Expand"} nested sections ({section.subsections.length})
                </p>
              )}
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(`/docs/${docId}/sections/${section.id}/edit`)}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => deleteSection(section.id)}
                className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>

          {isOpen && section.content && (
            <div
              className="prose prose-sm sm:prose max-w-none mt-3 text-slate-800"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(section.content),
              }}
            />
          )}
        </article>

        {isOpen && section.subsections?.length > 0 && (
          <ul className="space-y-3">
            {section.subsections.map((child) => renderSection(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  if (permissionLoading || loading) {
    return (
      <div className="flex justify-center mt-10">
        <div className="px-4 py-3 rounded-xl border border-slate-200 bg-white/90 text-slate-700 text-sm shadow-sm">
          Loading...
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          {permissionError || "Access denied."}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          Error: {error}
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex justify-center mt-10 px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-slate-200 bg-white/90 text-slate-700 text-sm shadow-sm text-center">
          Document not found.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <button
            type="button"
            onClick={() => navigate("/docs")}
            className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            Back to Docs
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-3">
            {doc.title || "Untitled Document"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage structure and section flow for this document.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(`/docs/${docId}/sections/create`)}
            className="rounded-full bg-slate-900 px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:bg-slate-800"
          >
            Add Section
          </button>
          <button
            type="button"
            onClick={() => navigate(`/docs/${docId}/edit`)}
            className="rounded-full border border-slate-300 px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Edit Document
          </button>
          <button
            type="button"
            onClick={deleteDoc}
            className="rounded-full bg-red-600 px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:bg-red-700"
          >
            Delete Document
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <aside className="xl:col-span-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm h-fit xl:sticky xl:top-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
            Outline
          </h2>
          {outline.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">No sections yet.</p>
          ) : (
            <ul className="mt-3 space-y-1.5 max-h-[60vh] overflow-auto pr-1">
              {outline.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#section-${item.id}`}
                    className="block rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                    style={{ marginLeft: `${item.depth * 10}px` }}
                  >
                    {item.title || `Section ${item.order}`}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="xl:col-span-3 space-y-4">
          {doc.description && (
            <article className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700 mb-3">
                Document Overview
              </h2>
              <div
                className="prose prose-sm sm:prose max-w-none text-slate-800"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(doc.description),
                }}
              />
            </article>
          )}

          {sections.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              No sections yet. Add one to organize this document.
            </div>
          ) : (
            <ul className="space-y-4">{sections.map((section) => renderSection(section, 0))}</ul>
          )}
        </main>
      </div>
    </div>
  );
};

export default InfoView;
